import type { Asset } from "schemas/generated/asset";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import {
  PopoverContent,
  PopoverHeader,
  PopoverRoot,
  PopoverTitle,
  PopoverTrigger,
} from "@replo/design-system/components/popover/Popover";
import AssetCardRenameForm from "@v2/features/assets/components/asset-card/AssetCardRenameForm";
import { analytics } from "@v2/features/infra/analytics/tracking";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { assetRouter, assetUtils } from "@v2/trpc/routes/asset";
import { Ellipsis, PenLine, Trash } from "lucide-react";

export const AssetCardActions: React.FC<{
  className?: string;
  asset: Asset;
  isMenuOpen: boolean;
  setIsMenuOpen: (isMenuOpen: boolean) => void;
  setActionPopoverContent: (
    actionPopoverContent: "delete" | "rename" | null,
  ) => void;
  actionPopoverContent: "delete" | "rename" | null;
}> = ({
  className,
  asset,
  isMenuOpen,
  setIsMenuOpen,
  setActionPopoverContent,
  actionPopoverContent,
}) => {
  const projectId = useCurrentProjectId();
  const { mutateAsync: deleteAsset } = assetRouter.delete.useMutation({
    onSuccess: () => {
      analytics.logEvent("asset.deleted", {
        projectId: projectId!,
        assetId: asset.id,
      });
      void assetUtils.list.invalidate();
      setIsMenuOpen(false);
      setActionPopoverContent(null);
    },
  });
  const { mutateAsync: updateAsset } = assetRouter.update.useMutation({
    onSuccess: () => {
      analytics.logEvent("asset.updated", {
        projectId: projectId!,
        assetId: asset.id,
      });
      void assetUtils.list.invalidate();
      setIsMenuOpen(false);
      setActionPopoverContent(null);
    },
  });

  const onDeleteAsset = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!projectId) {
      return;
    }

    await deleteAsset({
      assetId: asset.id,
      projectId,
    });
  };

  const onRenameAsset = async (assetName: string) => {
    if (!projectId) {
      return;
    }

    await updateAsset({
      assetId: asset.id,
      projectId,
      name: assetName,
    });
  };

  return (
    <PopoverRoot
      open={Boolean(actionPopoverContent)}
      onOpenChange={(open, event) => {
        // NOTE (Ben O, 2026-02-20): Only close on explicit actions (cancel/submit buttons),
        // not on outside-press or focus-out which fire when interacting with form inputs.
        if (!open && event.reason === "escape-key") {
          setActionPopoverContent(null);
        }
      }}
    >
      <PopoverContent
        side="right"
        align="end"
        sideOffset={4}
        showCloseButton={false}
        UNSAFE_className="flex flex-col"
      >
        <PopoverHeader>
          <PopoverTitle>
            {actionPopoverContent === "delete"
              ? "Delete asset"
              : "Rename asset"}
          </PopoverTitle>
        </PopoverHeader>
        <div className="flex flex-col gap-3">
          {actionPopoverContent === "delete" ? (
            <AssetCardDeleteForm
              onDeleteAsset={(e: React.MouseEvent<HTMLButtonElement>) => {
                return void onDeleteAsset(e);
              }}
              onCancel={() => setActionPopoverContent(null)}
            />
          ) : (
            <AssetCardRenameForm
              originalAssetName={asset.name}
              onRenameAsset={(assetName) => void onRenameAsset(assetName)}
            />
          )}
        </div>
      </PopoverContent>
      <PopoverTrigger UNSAFE_className={className}>
        <Menu
          open={isMenuOpen}
          onOpenChange={(open) => {
            setIsMenuOpen(open);
          }}
        >
          <MenuTrigger
            render={
              <Button
                icon
                variant="tertiary"
                size="sm"
                aria-hidden
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <Ellipsis />
              </Button>
            }
          />
          <MenuContent
            align="start"
            side="right"
            sideOffset={4}
            UNSAFE_className="w-40"
          >
            <MenuItem
              label="Rename"
              startEnhancer={<PenLine />}
              onClick={() => setActionPopoverContent("rename")}
            />
            <MenuItem
              label="Delete"
              startEnhancer={<Trash />}
              onClick={() => setActionPopoverContent("delete")}
            />
          </MenuContent>
        </Menu>
      </PopoverTrigger>
    </PopoverRoot>
  );
};

const AssetCardDeleteForm: React.FC<{
  onDeleteAsset: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onCancel: () => void;
}> = ({ onDeleteAsset, onCancel }) => {
  return (
    <>
      <div className="text-xs">
        If this asset is being used on a page, deleting it will break the asset
        until it is replaced.
      </div>
      <div className="flex gap-2">
        <Button variant="destructive" size="sm" onClick={onDeleteAsset}>
          Delete
        </Button>
        <Button variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </>
  );
};
